// Geolocation with the fallbacks plan.md §4.1 requires: GPS first, then an
// approximate location, then a manual place search — never a dead end.

import { useCallback, useEffect, useState } from 'react';
import { i18n } from './i18n.js';

const DEFAULT_CENTRE = { lat: 51.5074, lng: -0.1278, label: 'London' };

export function useGeolocation({ auto = true } = {}) {
  const [position, setPosition] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | locating | ok | denied | unavailable
  const [error, setError] = useState(null);

  const locate = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus('unavailable');
      setError(i18n.t('geo.noBrowser'));
      return;
    }
    setStatus('locating');
    setError(null);

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setPosition({
          lat: coords.latitude, lng: coords.longitude,
          accuracy: coords.accuracy, ts: Date.now(),
        });
        setStatus('ok');
      },
      (err) => {
        setStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable');
        setError(
          err.code === err.PERMISSION_DENIED
            ? i18n.t('geo.denied')
            : i18n.t('geo.fail'));
      },
      { enableHighAccuracy: false, timeout: 8_000, maximumAge: 300_000 });
  }, []);

  // Conditional high-accuracy refetch used by the corroboration gate: only
  // fires when the current fix is too stale or too coarse to clear the gate,
  // so a phone that already has a good GPS lock is not re-woken.  Errors are
  // swallowed on purpose — a failure just leaves the gate closed; it is never
  // worth interrupting someone reading a report.
  const ensureAccurateFix = useCallback(({ maxAgeMs = 5 * 60_000, maxAccuracyM = 0 } = {}) => {
    if (!navigator.geolocation) return;
    const fresh = position && Date.now() - (position.ts ?? 0) < maxAgeMs;
    const accurate = position?.accuracy != null
      && (maxAccuracyM <= 0 || position.accuracy <= maxAccuracyM);
    if (fresh && accurate) return;

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => setPosition({
        lat: coords.latitude, lng: coords.longitude,
        accuracy: coords.accuracy, ts: Date.now(),
      }),
      () => {},
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 });
  }, [position]);

  useEffect(() => {
    if (auto) locate();
  }, [auto, locate]);

  return { position, status, error, locate, ensureAccurateFix, defaultCentre: DEFAULT_CENTRE };
}

/**
 * Manual "search a place" fallback, via Nominatim.
 *
 * Only ever fired by an explicit user action, never automatically and never
 * per keystroke — Nominatim's usage policy discourages heavy automated use
 * (plan.md OQ9 makes the same point about reverse geocoding for the
 * leaderboard, which is why that stays "within 15 km" instead).
 */
export async function searchPlace(query) {
  const url = new URL('https://nominatim.openstreetmap.org/search');
  url.searchParams.set('q', query);
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('limit', '5');
  url.searchParams.set('addressdetails', '0');

  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(i18n.t('geo.searchUnavailable'));

  const results = await response.json();
  return results.map((r) => ({
    label: r.display_name,
    lat: Number(r.lat),
    lng: Number(r.lon),
  }));
}
